import { useState, type ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { useSetVersionStatus, type VersionOwner } from '@eis/core';
import { ConfirmAction, RequirePermission } from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import type { VersionLifecycle } from '../../tarifs/model/version-list';
import { ActivateVersionDialog } from './ActivateVersionAction';

/**
 * "Change the status of a version" (SDD-016 §4.2, D-V3) on the version screen, tarif AND group
 * tarif: a PUBLISHED version can be activated or retired; the ACTIVE one is in force and cannot be
 * retired until another is active (said, not hidden); a RETIRED version is terminal; a DRAFT is
 * edited in the draft editor.
 *
 * Drawn as design v2's outlined "Status ändern" menu (prompt 24 Phase 24.4): the transitions are
 * its entries, the one that is not allowed is shown disabled with the reason as its second line.
 * A draft keeps its single "Entwurf bearbeiten" button.
 *
 * Both dialogs say the two things SDD-005 §7 demands — what becomes active or retired, and that
 * nothing already in force moves — because the fear that a status change reprices live business is
 * what stops a rollback.
 */
export function VersionStatusActions({
  owner,
  slug,
  generation,
  version,
  lifecycle,
  onEditDraft,
}: {
  readonly owner: VersionOwner;
  readonly slug: string;
  readonly generation: string;
  /** The version's number (`1.2`) — its name. */
  readonly version: string;
  readonly lifecycle: VersionLifecycle;
  readonly onEditDraft: () => void;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const setStatus = useSetVersionStatus();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [confirming, setConfirming] = useState<'activate' | 'retire' | null>(null);
  const publish = owner === 'tarif' ? p.products.publish : p.groupTariffs.publish;
  const update = owner === 'tarif' ? p.products.update : p.groupTariffs.update;
  const move = (status: 'VERSION_STATUS_ACTIVE' | 'VERSION_STATUS_RETIRED'): void => {
    setStatus.mutate({ owner, slug, generation, version, status });
  };
  const close = (): void => {
    setAnchor(null);
  };

  if (lifecycle === 'DRAFT') {
    return (
      <RequirePermission anyOf={[update]}>
        <Button variant="outlined" onClick={onEditDraft}>
          {t('authoring.draft.open')}
        </Button>
      </RequirePermission>
    );
  }
  if (lifecycle === 'RETIRED' || lifecycle === 'DISCARDED') return null;

  return (
    <RequirePermission anyOf={[publish]}>
      <Stack spacing={1} sx={{ alignItems: 'flex-end' }}>
        <Button
          variant="outlined"
          endIcon={<ExpandMoreIcon />}
          aria-haspopup="menu"
          aria-expanded={anchor !== null}
          disabled={setStatus.isPending}
          onClick={(event) => {
            setAnchor(event.currentTarget);
          }}
        >
          {t('tarifs.versions.statusChange')}
        </Button>
        <Menu
          anchorEl={anchor}
          open={anchor !== null}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {lifecycle === 'PUBLISHED' && (
            <MenuItem
              onClick={() => {
                close();
                setConfirming('activate');
              }}
            >
              {t('authoring.activate.action')}
            </MenuItem>
          )}
          <MenuItem
            // The active version is in force: it cannot be retired until another one is.
            disabled={lifecycle === 'ACTIVE'}
            onClick={() => {
              close();
              setConfirming('retire');
            }}
            sx={{ color: 'warning.main', '&.Mui-disabled': { opacity: 1 } }}
          >
            <ListItemText
              primary={t('authoring.retire.action')}
              secondary={lifecycle === 'ACTIVE' ? t('authoring.retire.activeFirst') : undefined}
              slotProps={{
                // A disabled entry keeps its reason legible: the item is dimmed, its words are not.
                primary: { sx: { opacity: lifecycle === 'ACTIVE' ? 0.6 : 1 } },
                secondary: { sx: { whiteSpace: 'normal', maxWidth: 280 } },
              }}
            />
          </MenuItem>
        </Menu>
        {setStatus.error !== null && <Alert severity="error">{setStatus.error.message}</Alert>}
      </Stack>

      <ActivateVersionDialog
        version={version}
        open={confirming === 'activate'}
        busy={setStatus.isPending}
        onCancel={() => {
          setConfirming(null);
        }}
        onActivate={() => {
          setConfirming(null);
          move('VERSION_STATUS_ACTIVE');
        }}
      />
      {/*
        Retiring is terminal, so the dialog is marked destructive — and still says that the
        contracts already on the version keep resolving (D-V3: RETIRED forbids NEW pins, never
        existing ones).
      */}
      <ConfirmAction
        open={confirming === 'retire'}
        severity="destructive"
        title={t('authoring.retire.title', { version })}
        description={t('authoring.retire.description', { version })}
        confirmLabel={t('authoring.retire.confirm')}
        busy={setStatus.isPending}
        onCancel={() => {
          setConfirming(null);
        }}
        onConfirm={() => {
          setConfirming(null);
          move('VERSION_STATUS_RETIRED');
        }}
      >
        <Alert severity="info">{t('authoring.retire.noPolicyMoves', { version })}</Alert>
      </ConfirmAction>
    </RequirePermission>
  );
}
